import { makeAutoObservable, toJS } from "mobx"
import MeetingsApi from "./MeetingsApi"
import { Activity } from "./Activity"
import Notifications from "../../Notifications/Notifications"


/**
 * @typedef MeetingPlayer
 * @type {object}
 * @property {string} UserID l'id du user en mongo
 * @property {string} SessionID
 * @property {string} Pseudo
 * @property {number} Progress
 * @property {boolean} IsObserver
 * @property {boolean} IsGamemaster
 */


/**  @typedef {"SYNCHRO"|"DEFERRED_SEQUENTIAL"|"FREE"|"DEFAULT_SEQUENCER_MODE"} SequencerModes */



/**
 * Permet de modeliser un Meeting serveur, et d'observer certaines de ses propriétés afin de l'utiliser dans l'UI
 * Quand on obtient cet objet, on s'est déja connecté au meeting en tant que joueur/gamemaster
 */
export class Meeting {
  AdminUsersId = {}
  /** @type {Object.<string, MeetingPlayer>} */
  meetingPlayers = {}

  // TODO essayer de remplacer cela par des CustomEvents plutôt, cf ligne 629
  // ca permet de s'abonner de plusieurs endroits côté frontend
  onMeetingMessage = null
  onMeetingDataChange = null

  onUserListUpdate = null
  onUserConnect = null
  onUserDisconnect = null
  onUserChangePseudo = null

  /** @type {import("./MeetingsApi").VertxClientDef} */
  vertxClient = null

  sequencerMode = null

  sequencerActivated = undefined

  /** @type {SequencerModes} */
  sequencerDefaultMode = undefined

  _id = null
  MeetingName = null
  MeetingCode = null
  MeetingCodeDate = null

  get url() {
    let url = new URL(window.location)

    url.searchParams.delete("debug")
    url.searchParams.append("code", this.MeetingCode)

    return url.toString()
  }

  get players() {
    return this.meetingPlayersArray.filter(p => !p.IsObserver)
  }
  get observers() {
    return this.meetingPlayersArray.filter(p => p.IsObserver)
  }

  get meetingPlayersArray() {
    return Object.values(this.meetingPlayers)
  }

  get imMeetingAdmin() {
    let adminMeetingsIds = Object.keys(this.AdminUsersId)
    let myId = this.vertxClient.UserData.getUserID()
    return adminMeetingsIds.includes(myId)
  }

  /**
   *
   * @param {MeetingsApi} api
   * @param {{_id, MeetingName, MeetingCode, MeetingCodeDate, State}} meetingJson
   */
  constructor(vertxClient, meetingJson) {

    console.log("meetingJson", meetingJson)
    Object.assign(this, meetingJson)

    this.vertxClient = vertxClient

    /**
     * @type {Activity}
     */
    this.rootActivity = null

    this.OnMessageReceived = this.OnMessageReceived.bind(this)

    this.vertxClient.SIG_MeetingMessage.Add(this.OnMessageReceived)

    this.getPlayers()
    .catch(err => console.log("ERROR getPlayers", err))

    this.getSequencerMode()
    .catch(err => console.log("ERROR getSequencerMode", err))


    this.getSequencerActivated()
    .catch(err => console.log("ERROR getSequencerActivated", err))


    this.setSequencerDefaultMode("DEFERRED_SEQUENTIAL")
    .catch(err => console.log("ERROR getSequencerDefaultMode", err))

    makeAutoObservable(this, {
      vertxClient: false
    })
  }

  getConfig() {
    return new Promise( (resolve, reject) => {
      this.vertxClient.UserMeetingGetConfig(this._id, (success, message, data) => {
        if(success) {
          try {
            let config = JSON.parse(data.DataArray[0])
            resolve(config)
          }
          catch(err) {
            reject(err)
          }
        }
        else {
          reject(message)
        }
      })
    })
  }

  disconnect() {

    return new Promise( (resolve, reject) => {
      this.vertxClient.UserMeetingDisconnect(this._id, (success, message, data) => {
        if(success) {
          this.vertxClient.SIG_MeetingMessage.Remove(this.OnMessageReceived)
          resolve(data)
        }
        else {
          reject(message)
        }

      })
    })
  }

  // renvoie les joueurs du meeting
  getPlayers() {
    return new Promise( (resolve, reject) => {
      this.vertxClient.UserMeetingGetConnectedPlayers(this._id, (success, msg, data) => {
        if(success) {
          let meetingPlayers = {}
          if(data.DataArray) {
            data.DataArray.forEach(json => {
              // attention, dans la clé "_id", c'est en fait une SessionID, il y a aussi la clé SessionID pour être sur :)
              let p = JSON.parse(json) // {_id: sessionID, SessionID, Pseudo, UserID, IsObserver}
              let SessionID = p._id

              let isGm = Object.keys(this.AdminUsersId).includes(p.UserID)

              /** @type {MeetingPlayer} */
              let player = {
                UserID: p.UserID,
                SessionID,
                Pseudo: p.Pseudo,
                IsObserver: p.IsObserver,
                Progress: 0,
                IsGamemaster: isGm
              }
              meetingPlayers[SessionID] = player
            })
          }
          this.meetingPlayers = meetingPlayers
          resolve()
        }
        else {
          reject(msg)
        }
      })
    })
  }

  /**
   * permet de récupérer la liste des activities de ce meeting.
   * on ne l'appelle pas automatiquement, peut-être il n'y en a pas, ou on veut garder ça côté serveur
   *    *
   * @returns {Promise<Meeting>}
   */
  getActivities(isBackup = false) {
    var cmd = {
      ActivityManager: {
        Command: 'GetConfig',
        WithInitdata: true,
        isBackup: isBackup
      }
    }
    return this._sendCommand(cmd)
    .then(data => {
      let parsed = JSON.parse(data.DataArray[0])
      return parsed
    })
    .then(data => {
      this.rootActivity = new Activity(data, null, this)
      return this
    })
    .catch(err => {
      console.log("GetConfig", err)
    })
  }

  clearActivities() {
    var cmd = {
      ActivityManager: {
        Command: 'Clear'
      }
    }
    return this._sendCommand(cmd)
  }

  getEnabledActivities() {
    var cmd = {
      ActivityManager: {
        Command: 'GetEnabled'
      }
    }
    return this._sendCommand(cmd)
    .then(data => JSON.parse(data.DataArray[0]))
  }

  getOpenedActivities() {
    var cmd = {
      ActivityManager: {
        Command: 'GetOpened'
      }
    }
    return this._sendCommand(cmd)
    .then(data => JSON.parse(data.DataArray[0]))
  }

  // ? renvoie les joueurs d'une activité ? a documenter
  getActivityPlayers() {
    var cmd = {
      ActivityManager: {
        Command: 'GetPlayers'
      }
    }
    return this._sendCommand(cmd)
    .then(data => JSON.parse(data.DataArray[0]))
  }

  /**
   * Permet de changer le mode de sequencement global du meeting.
   * Cette valeur peut être overridée par chaque activity pour le sequencement de ses enfants
   * !! Attention à ne pas confondre avec setSequencerMode qui va changer de nom bientôt
   * @param {SequencerModes} value
   * @returns {Promise<SequencerModes>}
   */
  setSequencerDefaultMode(value) {
    var cmd = { ActivityManager: { Command: 'SequencerDefaultMode', Value: value } }

    return this._sendCommand(cmd)
    .then(res => this._parseReturn(res))
    .then(value => {
      console.log("setSequencerDefaultMode VALUE", value)
      this.sequencerDefaultMode = value
      return value
    })
  }

  /** @returns {SequencerModes} */
  getSequencerDefaultMode() {
    var cmd = { ActivityManager: { Command: 'SequencerDefaultMode'} }
    return this._sendCommand(cmd)
    .then(res => this._parseReturn(res))
    .then(value => {
      console.log("getSequencerDefaultMode VALUE", value)
      this.sequencerDefaultMode = value
      return value
    })
  }

   /**
    * Permet de changer le mode de sequencement.
    * MANUAL : Il faut faire Next à la main après chaque activity
    * SEMI-AUTO, elles s'enchainent en automatique sauf si elle ont un flag SequencerAutoOpenNext à false
    * AUTO: elles s'enchainent en automatique quel que soit le flag
    * @param {"MANUAL"|"SEMI_AUTO"|"AUTO"} value
    * @returns
    */
  setSequencerMode(value) {
    let cmd = {
      ActivityManager: {
        Command: 'SetSequencerAutoMode',
        Value: value
      }
    }
    return this._sendCommand(cmd)
    .then(() => this.getSequencerMode())
  }

   /**
    * Permet de récupérer le mode de sequencement.
    * MANUAL : Il faut faire Next à la main après chaque activity
    * SEMI_AUTO, elles s'enchainent en automatique sauf si elle ont un flag SequencerAutoOpenNext à false
    * AUTO: elles s'enchainent en automatique quel que soit le flag
    * @returns
    */
  getSequencerMode() {
    let cmd = {
      ActivityManager: {
        Command: 'GetSequencerAutoMode'
      }
    }
    return this._sendCommand(cmd)
    .then(res => this._parseReturn(res))
    .then(val => {
      this.sequencerMode = val
      return val
    })

  }

  activateSequencer() {
    let cmd = {
      ActivityManager: {
        Command: 'ActivateSequencer'
      }
    }
    return this._sendCommand(cmd)
    .then(() => this.getSequencerActivated())
  }

  getSequencerActivated() {
    let cmd = {
      ActivityManager: {
        Command: 'GetSequencerActivated'
      }
    }
    return this._sendCommand(cmd)
    .then(res => this._parseReturn(res))
    .then(val => {
      console.log("getSequencerActivated", val)
      this.sequencerActivated = val === 1
    })
  }
  sequencerNext() {
    let cmd = {
      ActivityManager: {
        Command: 'MovePlayersAfterNextSynchro'
      }
    }
    return this._sendCommand(cmd)
  }

  // CI DESSOUS, les fonctions liées à 1 activity.
  completeActivity(activity_id) {
    let cmd = {
      ActivityManager: {
        Command: 'ActivityComplete',
        Activity: activity_id
      }
    }
    return this._sendCommand(cmd)
  }


  // TODO Peut-être les bouger dans Activity ???????????????
  enableActivity(activity_id, value) {
    let cmd = {
      ActivityManager: {
        Command: 'Enable',
        Activity: activity_id,
        Value: value
      }
    }
    return this._sendCommand(cmd)
  }

  openActivity(activity_id, value) {
    let cmd = {
      ActivityManager: {
        Command: 'Open',
        Activity: activity_id,
        Value: value,
      }
    }

    return this._sendCommand(cmd)
    .then(data => data?.DataArray?.[0])
  }

  openNextActivity(current_activity_id, closeCurrent=false) {
    let cmd = {
      ActivityManager: {
        Command: 'OpenNext',
        Activity: current_activity_id,
        CloseCurrent: closeCurrent
      }
    }
    return this._sendCommand(cmd)
    .then(data => {
      if(data?.DataArray) {
        return data.DataArray[0]
      }
    })

  }

  moveActivity(activity_id, before, after ) {
    if(!before && !after) return Promise.reject("Either before or after should be set")
    if(before && after) return Promise.reject("You can't set before AND after")

    let cmd = {
      ActivityManager: {
        Command: 'Move',
        Activity: activity_id,
      }
    }
    if(after) cmd.ActivityManager.After = after
    if(before) cmd.ActivityManager.Before = before


    return this._sendCommand(cmd)
  }

  parentActivity(activity_id, parent) {
    let cmd = {
      ActivityManager: {
        Command: 'Parent',
        Activity: activity_id,
        Parent: parent
      }
    }
    return this._sendCommand(cmd)
  }

  enterActivity(activity_id) {
    var cmd = {
      Activity: {
        NetID: activity_id,
        Command: 'PlayerEnter'
      }
    }

    return this._sendCommand(cmd)
  }

  leaveActivity(activity_id) {
    var cmd = {
      Activity: {
        NetID: activity_id,
        Command: 'PlayerLeave'
      }
    }

    return this._sendCommand(cmd)
  }

  getFirstLeaf(activity_id) {
    var cmd = {
      Activity: {
        NetID: activity_id,
        Command: 'GetFirstLeaf'
      }
    }

    return this._sendCommand(cmd)
    .then(data => data.DataArray[0])
  }


  progressActivity(activity_id, progress) {
    let cmd = {
      Activity: {
        NetID : activity_id,
        Command: 'PlayerProgress',
        Progress : progress
      }
    }

    return this._sendCommand(cmd)
  }

  findEntryPoint(prefferedEntryPointNetID) {
    let cmd = {
      ActivityManager: {
        Command: 'FindEntryPoint',
      }
    }
    if(prefferedEntryPointNetID) cmd.ActivityManager.TryThisEntry = prefferedEntryPointNetID

    return this._sendCommand(cmd)
  }

  isSequencerValid() {
    let cmd = {
      ActivityManager: {
        Command: 'IsSequencerValid'
      }
    }
    return this._sendCommand(cmd)
    .then(res => this._parseReturn(res))
  }

  ////////// Commandes PlateauDeplacementActivity //////////
  sendChosenCell(activity_id, cell_id) {
    let cmd = {
      Activity: {
        NetID : activity_id,
        Command: "PlayerGoToCell",
        CellID: cell_id
      }
    }

    return this._sendCommand(cmd)
    .then(res => JSON.parse(res.DataArray[0]))
  }

  ///////////////////////////////////////////////////////////////////////////
  // permet de ne pas pourrir l'api avec des commandes custom pour les activities custom
  customActivityCommand(commandName, netId, parseReturn = false, additionalParameters = {} ) {
    let cmd = {
      Activity: {
        NetID : netId,
        Command: commandName,
        ...additionalParameters
      }
    }
    return this._sendCommand(cmd)
    .then(res => {
      if(parseReturn) {
        return this._parseReturn(res)
      }
      else {
        return res
      }
      // console.log("customActivityCommand", commandName, val)

    })
  }

  _sendCommand(cmd) {
    return new Promise( (resolve, reject) => {
      this.vertxClient.UserMeetingCommand(this._id, "GameModule", JSON.stringify(cmd), (success, msg, data) => {
        // console.log("==================")
        // console.log("SEND COMMAND", cmd)
        // console.log("success", success)
        // console.log("msg", msg)
        // console.log("data", data)
        // console.log("==================")
        if(success) {
          resolve(data)
        } else {
          console.error("Error UserMeetingCommand : " + JSON.stringify(cmd) + " -> " + msg)
          reject(msg)
        }
      })
    })
  }

  _parseReturn(res) {
    try {
      let val = res.DataArray[0]
      val = JSON.parse(val)
      return val
    } catch (error) {
      // console.log("in catch", res.DataArray[0])
      if(res?.DataArray?.[0]) {
        return res.DataArray[0]
      }
      return null
    }
  }

  /********* Meeting Custom Datas ****************************** */

  getMeetingData(dataId) {
    return new Promise( (resolve, reject) => {
      this.vertxClient.UserMeetingCustomDataGetItem(this._id, dataId, (success, msg, data) => {
        if(success) {
          resolve(data)
        } else {
          reject(msg)
        }
      })
    })
    .then(res => this._parseReturn(res))
  }

  setMeetingData(dataId, data) {
    return new Promise( (resolve, reject) => {
      this.vertxClient.UserMeetingCustomDataSetItem(this._id, dataId, JSON.stringify(data), false, true, (success, msg, data) => {
          if(success) {
            resolve(data)
          } else {
            reject(msg)
          }
        })
      })
  }

  removeMeetingData(dataId) {
    return new Promise( (resolve, reject) => {
      this.vertxClient.UserMeetingCustomDataRemoveItem(this._id, dataId, false, true, resolve())
    })
  }


  UserMeetingGetConnectedPlayers() {
    return new Promise( (resolve, reject) => {
      this.vertxClient.UserMeetingGetConnectedPlayers(this._id, (success, msg, data) => {
          if(success) {
            resolve(data)
          } else {
            reject(msg)
          }
        })
      })
  }

  UserMeetingGetPlayerID(id) {
    return new Promise( (resolve, reject) => {
      this.vertxClient.UserMeetingGetConnectedPlayers(id, (success, msg, data) => {
          if(success) {
            resolve(data)
          } else {
            reject(msg)
          }
        })
      })
  }

  /********* UTILS ****************************** */

  OnMessageReceived(meeting_id, from_user_id, msg) {
    // console.log("OnMessageReceived", msg)
    if(meeting_id !== this._id) return

    if (from_user_id === undefined) from_user_id = "SERVER";

    try {
      let m = JSON.parse(msg)
      // console.log("OnMessageReceived", m)

      if(m.Name === "Activity") {
         // message data
        let md = JSON.parse(m.Data)
        // console.log("md", md)

        /** @type {Activity} */
        let activity = this.findActivity(md.NetID)
        // console.log("activity", activity)
        if(!activity) return

        if(md.SequencerActivated !== undefined) {
          this.sequencerActivated = md.SequencerActivated
        }

        if(md.StatusEnabled !== undefined) {
          activity.setEnabled(md.StatusEnabled)
        }

        if(md.StatusOpened !== undefined) {
          activity.setOpened(md.StatusOpened)
        }

        if(md.StatusCreated !== undefined) {
          if(md.Type === "Synchro") {
            activity.Synchro = true
          }
        }


        if(md.TimeOpened !== undefined) {
          activity.setTimeOpened(md.TimeOpened)
        }
        if(md.TimeEntered !== undefined) {
          activity.setTimeEntered(md.TimeEntered)
        }
        if(md.TimeLeft !== undefined) {
          activity.setTimeLeft(md.TimeLeft)
        }

        // ------ deprecated ?
        if(md.TimeElapsed !== undefined) {
          // if(!activity.Children) console.log("from message => TimeElapsed", activity.Name, md.TimeElapsed)
          activity.setTimeElapsed(md.TimeElapsed)
        }

        if(md.ClosingIn !== undefined) {
          activity.setClosingIn(md.ClosingIn)
        }
        if(md.CloseTime !== undefined) {
          activity.CloseTime = md.CloseTime
        }
        // -------



        if(md.PlayersSessionID) {
          // mise à jour des joueurs de l'activity == la liste des joueurs a changé
          // L'array md.PlayersSessionID contient toutes les s_id des joueurs présents dans l'activity (certains étaient déjà là !)
          // exemple : {"NetID": "198a3ca0530-6a4a-825", "PlayersSessionID": ["689ca161a75474549d2d878c"]}
          activity.handlePlayersListUpdate(md.PlayersSessionID)
        }

        if(md.PlayerProgress) {

          // {Progress:0.35,SessionID:"649da839da75004e9edaa7ac"}
          let {SessionID, Progress} = md.PlayerProgress
          let pseudo = this.meetingPlayers[SessionID]?.Pseudo
          // console.log(pseudo, "a envoyé un progress de", Progress, "dans", activity.Name)

          // on remplace dans les players de l'activity
          let playerData = activity.Players[SessionID]
          if(playerData) {
            let newData = {...playerData, Progress}
            activity.Players = {...activity.Players, [SessionID]: newData}
          }

          // console.log("activity.Players", toJS(activity.Players))

          // on remplace dans les players du meeting
          // let m_player = this.meetingPlayers[SessionID]
          // if(m_player) {
          //   let newData = {...m_player, Progress}
          //   this.meetingPlayers = {...this.meetingPlayers, [SessionID]: newData}
          // }


        }

        if(md.StatusMoved) {
          this.getActivities()
          .then(data => {
            this.rootActivity = new Activity(data, null)
          })
        }

        if(md.activity_inserted) {
          md.activity_inserted.forEach(activity_msg => {
            try {

              let data = JSON.parse(activity_msg.activity)

              let activityToInsert
              if(activity_msg.parent) {
                let parent = this.findActivity(activity_msg.parent)
                activityToInsert = new Activity(data, parent)
              }
              else {
                activityToInsert = new Activity(data, null)
              }


              if(activity_msg.previous) {

                // ici il faut mettre la nouvelle activité APRES celle-ci
                let prev = this.findActivity(activity_msg.previous)
                console.log("=========== insert activity après", prev.Name)
                let i = prev.Parent.Children.findIndex(c => c.NetID === prev.NetID)
                prev.Parent.Children.splice(i + 1, 0, activityToInsert) // on insere après (i+1)
              }
              else if (activity_msg.next) {
                // ici il faut mettre la nouvelle activité AVANT celle-ci
                let next = this.findActivity(activity_msg.next)
                console.log("=========== insert activity avant", next.Name)
                let i = next.Parent.Children.findIndex(c => c.NetID === next.NetID)
                next.Parent.Children.splice(i, 0, activityToInsert) // on insere avant (i)
              }
              else if(activity_msg.parent) {
                // ici il faut push dans md.parent.Children
                let parent = this.findActivity(activity_msg.parent)
                console.log("=========== insert activity a la fin des children de ", parent.Name)
                if(!parent.Children) parent.Children = []
                parent.Children.push(activityToInsert)
              }

            }
            catch(err) {
              Notifications.error('Message activity_inserted error :' + err)
            }

          })

          // console.log("this.rootActivity", toJS(this.rootActivity))
        }


        const evt = new CustomEvent("activityMessage", {
          detail: {
            activity,
            message: md
          }
        })
        window.dispatchEvent(evt)

      }

      else if(m.Name === "UserStatus") {
        // message data
        let md = JSON.parse(m.Data)
        // console.log("md", md)

        if(md.Type === "Connection" && md.Connected) {
          // {
          //   Connected: true,
          //   Pseudo: "gildas",
          //   Type: "Connection",
          //   SessionID: "650073b56243bb2e856d09df,
          //   IsObserver: true,
          //   UserID: "..."
          // }

          let isGm = Object.keys(this.AdminUsersId).includes(md.UserID)

          /** @type {MeetingPlayer} */
          let newPlayer = {
            UserID: md.UserID,
            SessionID: md.SessionID,
            Pseudo: md.Pseudo,
            IsObserver: md.IsObserver,
            Progress: 0,
            IsGamemaster: isGm
          }

          //qqn se connecte
          this.meetingPlayers = {
            ...this.meetingPlayers,
            [md.SessionID]: newPlayer
          }

          if(this.onUserConnect) this.onUserConnect(md.SessionID)
        }

        if(md.Type === "Connection" && !md.Connected) {
          if(this.onUserConnect) this.onUserDisconnect(md.SessionID)

          delete this.meetingPlayers[md.SessionID]

          // qqn se déconnecte
        }

        if(md.Type === "Pseudo") {
          let p = this.meetingPlayers[md.SessionID]
          if(p) {

            /** @type {MeetingPlayer} */
            let newPlayer = {
              ...p,
              Pseudo: md.Pseudo
            }

            this.meetingPlayers = {...this.meetingPlayers, [md.SessionID]: newPlayer}

            if(this.onUserChangePseudo) onUserChangePseudo(md.SessionID, md.Pseudo)
          }
        }

        if(this.onUserListUpdate) this.onUserListUpdate(this.meetingPlayers)

      }
      else if(m.Name === "Context") {
        let md = JSON.parse(m.Data)
        const evt = new CustomEvent("contextMessage", {
          detail: {
            context: md.Value.RuntimeContext || md.Value
          }
        })
        window.dispatchEvent(evt)
      }
      else if(m.Name === "CustomData") {
        let md = JSON.parse(m.Data)

        if(this.onMeetingDataChange) {
          this.onMeetingDataChange(md.Key, md.Value)
        }

        const evt = new CustomEvent("meetingCustomData", {
          detail: {
            key: md.Key,
            value: md.Value
          }
        })
        window.dispatchEvent(evt)
      }
      else {
        let md = JSON.parse(m.Data)
        const evt = new CustomEvent("meetingMessage", {
          detail: md
        })
        window.dispatchEvent(evt)

        if(this.onMeetingMessage) this.onMeetingMessage(m)
      }
    }
    catch(err) {
      console.log("err", err)
      // console.log("original message",msg)
    }
  }

  /**
   *
   * @param {string|Function} func
   * @param {*} activity
   * @returns {Activity}
   */
  findActivity (func, activity = this.rootActivity) {

    // helper, si on passe une NetID, on crée la func a la volée
    if(typeof func === "string") {
      let act_id = func
      func = (a) => a.NetID === act_id
    }
    if(!activity) return null

    if(func(activity)) {
      return activity
    }

    if(activity.Children) {

      let index = 0
      let act = null

      while(act === null && index < activity.Children.length) {
        act = this.findActivity(func, activity.Children[index])
        index += 1
      }
      return act
    }
    return null
  }
  filterActivities (func, activity = this.rootActivity, result = []) {
    if(!activity) return null

    if(func(activity)) {
      result.push(activity)
    }

    if(activity.Children) {

      let index = 0
      let act = null

      while(act === null && index < activity.Children.length) {
        this.filterActivities(func, activity.Children[index], result)
        index += 1
      }
    }
    return result
  }
  /**
   *
   * @param {activity} NetID
   * @returns {Activity}
   */
  getNextActivity(activity) {
    let flag = false
    let ordered =this.getOrderedNodes()

    for(let i in ordered) {
      let a = ordered[i]
      if(!a.Enabled) continue
      if(flag && !a.Children) {
        return a
      }
      if (a === activity) {
        flag = true
      }
    }

    return null
  }

  getOrderedNodes() {
    let nodes = []

    function recurse(currentNode) {
      if(!currentNode.Enabled) return
      nodes.push(currentNode)
      if(currentNode.Children) {
        currentNode.Children.forEach(child => {
          recurse(child);
        })
      }
    }

    recurse(this.rootActivity)
    return nodes
  }


}